import { useState } from "react";
import { useMutation } from "@apollo/client/react";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CREATE_CLIENTS_MAX_ROWS } from "@athena/common/utils/clientImport";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { SendClientWelcomeEmailsDocument } from "@/graphql/clients.generated";
import type { ClientRecord } from "./client";

/** Whoever can be sent a welcome email and has not been sent one: what "everyone not yet emailed" means. */
export const notYetWelcomed = (clients: ClientRecord[]): ClientRecord[] =>
    clients.filter((client) => !client.welcomeEmailBlocker && !client.welcomeEmailSentAt);

/**
 * Sends welcome emails and says how it went (ATH-244): the one send behind the profile's button,
 * the directory's and the import's. Refreshes the clients on screen, so a sent date and the
 * not-yet-emailed count update at once.
 *
 * `sendWelcomeEmails` sends to one person on the click, and asks first before sending to more: "Are you
 * sure you want to send a welcome email to **N alumni**?". The confirmation lives here rather than
 * at each button, so no way of sending to many can skip it. Render `confirmDialog` where the button is.
 */
export const useSendWelcomeEmails = () => {
    const { t } = useTranslation();
    const [
        send,
        { loading },
    ] = useMutation(SendClientWelcomeEmailsDocument, { refetchQueries: "active" });
    const [
        pending,
        setPending,
    ] = useState<{ clientIds: string[]; onSent?: () => void } | undefined>(undefined);

    /** Resolves to whether the request itself went through. */
    const sendNow = async (clientIds: string[]): Promise<boolean> => {
        try {
            // The API takes one import's worth per call (`MAX_WELCOME_RECIPIENTS`).
            const result = { sentCount: 0, failedCount: 0, skipped: [] as unknown[] };
            for (let start = 0; start < clientIds.length; start += CREATE_CLIENTS_MAX_ROWS) {
                const { data } = await send({
                    variables: { clientIds: clientIds.slice(start, start + CREATE_CLIENTS_MAX_ROWS) },
                });
                const batch = data?.sendClientWelcomeEmails;
                if (!batch) throw new Error("No result");
                result.sentCount += batch.sentCount;
                result.failedCount += batch.failedCount;
                result.skipped.push(...batch.skipped);
            }
            const summary = [
                t("admin.alumni.welcome.sent", { count: result.sentCount }),
                result.skipped.length > 0 ? t("admin.alumni.welcome.skipped", { count: result.skipped.length }) : null,
                result.failedCount > 0 ? t("admin.alumni.welcome.failedSome", { count: result.failedCount }) : null,
            ]
                .filter(Boolean)
                .join(" ");
            if (result.sentCount > 0) toast.success(summary);
            else toast.warning(summary);
            return true;
        } catch {
            toast.error(t("admin.alumni.welcome.failed"));
            return false;
        }
    };

    /** Sends to one person at once; asks before sending to more. `onSent` runs once the request went through. */
    const sendWelcomeEmails = async (clientIds: string[], onSent?: () => void): Promise<void> => {
        if (clientIds.length > 1) {
            setPending({ clientIds, onSent });
            return;
        }
        if (await sendNow(clientIds)) onSent?.();
    };

    const count = pending?.clientIds.length ?? 0;
    const confirmDialog = (
        <ConfirmDialog
            open={pending !== undefined}
            onOpenChange={(open) => {
                if (!open) setPending(undefined);
            }}
            title={t("admin.alumni.welcome.confirmTitle")}
            description={
                <Trans
                    i18nKey="admin.alumni.welcome.confirmDescription"
                    count={count}
                    components={{ bold: <strong className="text-foreground" /> }}
                />
            }
            confirmLabel={t("admin.alumni.welcome.confirmLabel", { count })}
            loadingLabel={t("admin.alumni.welcome.sending")}
            loading={loading}
            onConfirm={async () => {
                if (!pending) return;
                const sent = await sendNow(pending.clientIds);
                setPending(undefined);
                if (sent) pending.onSent?.();
            }}
        />
    );

    return { sendWelcomeEmails, sending: loading, confirmDialog };
};
